import { hasPermission, type SessionUser } from '@fc/core'
import { useQuery } from '@tanstack/react-query'
import { me } from './api'

/**
 * TanStack Query key for the authenticated principal (GET /auth/me). Shared so
 * login/logout flows can seed or invalidate the cached session.
 */
export const SESSION_KEY = ['auth', 'me'] as const

/**
 * Fetch the current {@link SessionUser} (identity + effective permission keys)
 * from `/auth/me`. Held in the query cache so every consumer shares one request.
 *
 * A 401 (anonymous) is a normal, expected outcome here — callers treat the
 * absence of a session as "no permissions" rather than an error — so we do not
 * retry on failure.
 */
export function useSession() {
  return useQuery<SessionUser>({
    queryKey: SESSION_KEY,
    queryFn: me,
    retry: false,
    staleTime: 60_000,
  })
}

/**
 * The effective permission keys for the active session, or an empty array while
 * loading / when anonymous. Use with {@link hasPermission} (or {@link useCan}).
 */
export function usePermissions(): string[] {
  const { data } = useSession()
  return data?.permissions ?? []
}

/**
 * Convenience guard: does the current session satisfy `permission`?
 *
 * An undefined `permission` means "ungated" → always visible. This is the
 * client disclosure layer only; the API still enforces authz server-side.
 */
export function useCan(permission?: string): boolean {
  const permissions = usePermissions()
  if (!permission) return true
  return hasPermission(permissions, permission)
}
